import * as THREE from 'three';
import type { GroundPreset } from '../../../core/config/theme';
import { ARENA } from '../../../core/config/gameConfig';

// Builds the arena floor from a theme's GroundPreset. The mesh is usable
// immediately (tinted, gently noised so it isn't dead flat); diffuse and
// displacement textures stream in asynchronously and upgrade the material in
// place. Swapping ground per level = dispose old mesh, createGround(new preset).

/** Subtle random vertex height so the plane reads as terrain pre-texture. */
function addHeightNoise(geometry: THREE.PlaneGeometry, amplitude: number): void {
  const pos = geometry.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    pos.setY(i, pos.getY(i) + (Math.random() * 2 - 1) * amplitude);
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
}

// The 129×129-vertex noised plane is identical across themes (only materials
// differ), so it is built once and shared by every ground mesh.
let groundGeometry: THREE.PlaneGeometry | null = null;

function getGroundGeometry(): THREE.PlaneGeometry {
  if (!groundGeometry) {
    groundGeometry = new THREE.PlaneGeometry(100, 100, 128, 128);
    groundGeometry.rotateX(-Math.PI / 2); // lay flat first, so noise displaces world-Y
    addHeightNoise(groundGeometry, 0.35);
    groundGeometry.userData.shared = true;
  }
  return groundGeometry;
}

/** Async texture load guarded against the material being disposed first —
 *  fast theme transitions used to leak the late-arriving GPU texture. */
function loadTiled(
  url: string,
  repeat: number,
  srgb: boolean,
  material: THREE.Material,
  assign: (tex: THREE.Texture) => void,
): void {
  new THREE.TextureLoader().load(url, (tex) => {
    if (material.userData.disposed) {
      tex.dispose();
      return;
    }
    if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(repeat, repeat);
    assign(tex);
  });
}

export function createGround(preset: GroundPreset): THREE.Mesh {
  const material = new THREE.MeshStandardMaterial({
    color: preset.tint,
    roughness: 0.95,
    metalness: 0,
  });

  if (preset.textureUrl) {
    loadTiled(preset.textureUrl, preset.repeat, true, material, (tex) => {
      material.map = tex;
      material.needsUpdate = true;
    });
  }
  if (preset.displacementUrl) {
    // Displacement is height data, not color — keep it linear (non-sRGB).
    loadTiled(preset.displacementUrl, preset.repeat, false, material, (tex) => {
      material.displacementMap = tex;
      material.displacementScale = preset.displacementScale;
      // Center the displacement on the plane (map average ≈ mid-grey), so
      // terrain undulates around groundY instead of rising above it and
      // swallowing the player/enemies, which sit just above groundY.
      material.displacementBias = -preset.displacementScale * 0.5;
      material.needsUpdate = true;
    });
  }

  const mesh = new THREE.Mesh(getGroundGeometry(), material);
  mesh.position.y = ARENA.groundY;
  mesh.receiveShadow = true;
  return mesh;
}

/** Drifts the ground's textures under the player — diffuse AND displacement
 *  move together, so the GPU-displaced hills physically flow past. The mesh
 *  never moves; only UVs do, so the illusion costs nothing. */
export function scrollGround(mesh: THREE.Mesh, dt: number, uvPerSec: number): void {
  if (uvPerSec === 0) return;
  const mat = mesh.material as THREE.MeshStandardMaterial;
  const dy = uvPerSec * dt;
  if (mat.map) mat.map.offset.y -= dy;
  if (mat.displacementMap) mat.displacementMap.offset.y -= dy;
}

/** Frees a ground mesh's material and textures. The geometry is the shared
 *  singleton and is left alone. */
export function disposeGround(mesh: THREE.Mesh): void {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const material of materials) {
    const std = material as THREE.MeshStandardMaterial;
    std.userData.disposed = true; // cancels in-flight texture loads
    std.map?.dispose();
    std.displacementMap?.dispose();
    material.dispose();
  }
}
